// src/MailingFormPage.js
import React, { useState, useEffect, useRef } from "react";
import {
  ChevronDown, Search, Building2, Phone, MapPin,
  FileSpreadsheet, Calendar, ShieldCheck,
  AlertTriangle, Hash, Filter, Download
} from "lucide-react";
import {
  iniciarExportacaoAssincrona,
  verificarProgressoTask,
  erroDePollingTransitorio,
  baixarArquivoTask,
  listarCidadesTodas,
  listarCnaes,
  listarBairros
} from "./api";
import ExportStatusModal from "./components/ExportStatusModal";
import { VIDEO_TUTORIAL_EXTRACAO_ID } from "./config";
import VideoYouTube from "./components/VideoYouTube";
import "./MailingFormPage.css";
import { avisoDaLista, filtrarOpcoes } from "./buscaOpcoes";

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"
];
const TODAS_UFS = [...UF_LIST];
const BRASIL_TODO = "__BRASIL_TODO__";

const PORTE_OPTIONS = [
  "ME - Microempresa",
  "EPP - Empresa de Pequeno Porte",
  "DEMAS - Empresa de Médio ou Grande Porte",
];

const SITUACAO_CADASTRAL_OPTIONS = [
  "Ativa", "Baixada", "Inapta", "Suspensa", "Nula",
];

const DDD_LIST = [
  "11", "12", "13", "14", "15", "16", "17", "18", "19",
  "21", "22", "24", "27", "28",
  "31", "32", "33", "34", "35", "37", "38",
  "41", "42", "43", "44", "45", "46", "47", "48", "49",
  "51", "53", "54", "55",
  "61", "62", "63", "64", "65", "66", "67", "68", "69",
  "71", "73", "74", "75", "77", "79",
  "81", "82", "83", "84", "85", "86", "87", "88", "89",
  "91", "92", "93", "94", "95", "96", "97", "98", "99"
];

// ---------- MultiSelectDropdown ----------
function MultiSelectDropdown({ label, placeholder, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setFilter("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const busca = filtrarOpcoes(options, filter);
  const limitedOptions = busca.visiveis;
  const aviso = avisoDaLista(busca, filter);

  function toggleOption(opt) {
    if (opt === BRASIL_TODO) {
      onChange([...TODAS_UFS]);
      setOpen(false);
      setFilter("");
      return;
    }
    if (selected.includes(opt)) {
      onChange(selected.filter((o) => o !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  function removeTag(opt) {
    onChange(selected.filter((o) => o !== opt));
  }

  return (
    <div className="mf-field" ref={wrapperRef}>
      <span className="mf-label">{label}</span>
      <div
        className={`mf-select-box ${open ? "mf-select-box--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        {selected.length === 0 ? (
          <span className="mf-placeholder">{placeholder}</span>
        ) : (
          <div className="mf-chips">
            {selected.slice(0, 3).map((opt) => (
              <span key={opt} className="mf-chip">{opt}</span>
            ))}
            {selected.length > 3 && (
              <span className="mf-chip mf-chip--more">+{selected.length - 3}</span>
            )}
          </div>
        )}
        <div className="mf-select-actions" onClick={(e) => e.stopPropagation()}>
          {selected.length > 0 && (
            <button type="button" className="mf-clear-btn" onClick={() => onChange([])}>
              Limpar
            </button>
          )}
          <ChevronDown size={14} className={`mf-caret ${open ? "mf-caret--open" : ""}`} />
        </div>
      </div>

      {open && (
        <div className="mf-dropdown">
          <input
            type="text"
            className="mf-dropdown-search"
            placeholder="Digite para buscar..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="mf-dropdown-list">
            {limitedOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`mf-dropdown-item ${selected.includes(opt) ? "mf-dropdown-item--active" : ""}`}
                onClick={() => toggleOption(opt)}
              >
                <span className={`mf-check ${selected.includes(opt) ? "mf-check--on" : ""}`} />
                {opt}
              </button>
            ))}
          </div>
          <div className="mf-dropdown-footer">
            {aviso && <span className="mf-dropdown-aviso">{aviso}</span>}
            <button type="button" className="mf-dropdown-close" onClick={() => { setOpen(false); setFilter(""); }}>
              Fechar
            </button>
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div className="mf-tags">
          {selected.map((opt) => (
            <span key={opt} className="mf-tag">
              {opt}
              <button type="button" onClick={() => removeTag(opt)}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- HelpTooltip ----------
function HelpTooltip({ text }) {
  return (
    <span className="mf-tooltip">
      ?
      <span className="mf-tooltip-text">{text}</span>
    </span>
  );
}

// =================================================================
export default function MailingFormPage({ user }) {
  const groups = user?.groups || [];
  const isStaffReserva = Boolean(
    user?.is_staff || user?.is_superuser ||
    groups.includes("ADM") || groups.includes("SUPORTE")
  );
  // territorio_isento é o campo oficial do contrato (equipe interna vem com ele true e
  // ufs_permitidas: []); se o backend não mandar o campo, usa a heurística de reserva.
  const isStaff = user?.territorio_isento === undefined ? isStaffReserva : user.territorio_isento === true;
  const ufsPermitidas = Array.isArray(user?.ufs_permitidas) ? user.ufs_permitidas : [];
  const territorioAtivo = !isStaff && ufsPermitidas.length > 0;
  const territorioSemLiberacao = !isStaff && user?.ufs_permitidas !== undefined && ufsPermitidas.length === 0;
  const ufUnica = territorioAtivo && ufsPermitidas.length === 1;

  const [ufs, setUfs] = useState(() => (ufUnica ? [ufsPermitidas[0]] : []));
  const [municipios, setMunicipios] = useState([]);
  const [ddd, setDdd] = useState([]);
  const [cnaesSelecionados, setCnaesSelecionados] = useState([]);
  const [buscaTermo, setBuscaTermo] = useState("");
  const [tipoTelefone, setTipoTelefone] = useState("");
  const [porteEmpresa, setPorteEmpresa] = useState([]);
  const [meiNaomei, setMeiNaomei] = useState("NAOMEI");
  const [dataInicioAtiv, setDataInicioAtiv] = useState("");
  const [formato, setFormato] = useState("xlsx");
  const dateHiddenRef = useRef(null);
  const dateInputRef = useRef(null);
  const [situacaoCadastral, setSituacaoCadastral] = useState(["Ativa"]);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmSnapshot, setConfirmSnapshot] = useState(null);
  const [ufErroModalOpen, setUfErroModalOpen] = useState(false);
  const exportBtnRef = useRef(null);

  const confirmItens = [
    { key: "UF", value: (confirmSnapshot?.ufs || [confirmSnapshot?.uf]).filter(Boolean).join(", "), icon: MapPin },
    { key: "Cidades", value: confirmSnapshot?.municipios?.join(", "), icon: MapPin },
    { key: "Bairros", value: confirmSnapshot?.bairros?.join(", "), icon: MapPin },
    { key: "CNAE", value: confirmSnapshot?.cnae_fiscal?.join(", "), icon: Building2 },
    { key: "DDD", value: confirmSnapshot?.ddd?.join(", "), icon: Hash },
    { key: "Tipo telefone", value: confirmSnapshot?.tipo_telefone, icon: Phone },
    { key: "MEI", value: (confirmSnapshot?.MEINAOMEI && confirmSnapshot.MEINAOMEI !== "AMBOS") ? confirmSnapshot.MEINAOMEI : null, icon: Filter },
    { key: "Busca", value: confirmSnapshot?.busca_termo, icon: Search },
    { key: "Porte", value: confirmSnapshot?.porte_empresa?.join(", "), icon: Building2 },
    { key: "Situação cadastral", value: confirmSnapshot?.situacao_cadastral?.join(", "), icon: ShieldCheck },
    { key: "Início atividade ≤", value: confirmSnapshot?.data_inicio_atividades, icon: Calendar },
  ].filter((i) => i.value && String(i.value).trim().length > 0);

  const filtrosCount = confirmItens.length;
  const riscoAmplo = filtrosCount <= 1;
  const HeaderIcon = riscoAmplo ? AlertTriangle : ShieldCheck;

  const [listaBairros, setListaBairros] = useState([]);
  const [bairrosSelecionados, setBairrosSelecionados] = useState([]);
  const [carregandoBairros, setCarregandoBairros] = useState(false);
  const [cidadesPorUf, setCidadesPorUf] = useState({});
  const [cidadesDaUf, setCidadesDaUf] = useState([]);
  const [listaCnaes, setListaCnaes] = useState([]);
  const [loadingAuxiliares, setLoadingAuxiliares] = useState(false);
  const [cnaeModalAberto, setCnaeModalAberto] = useState(false);
  const [cnaeBusca, setCnaeBusca] = useState("");

  const [exportTaskId, setExportTaskId] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportState, setExportState] = useState(null); // running | success | vazio | failure
  const [exportMensagem, setExportMensagem] = useState("");
  const [exportRegistros, setExportRegistros] = useState(null);
  const [exportErro, setExportErro] = useState("");
  const [exportFileName, setExportFileName] = useState(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const isMountedRef = useRef(true);
  const pollTimerRef = useRef(null);
  const failCountRef = useRef(0);
  const firstFailAtRef = useRef(null);
  const MAX_POLL_TRANSIENT_MS = 5 * 60 * 1000;
  const userKey = user?.id ?? user?.email ?? user?.username ?? "anon";
  const STORAGE_KEY = `mailing_task_state_${userKey}`;

  function fecharModalExportacao() {
    setExportModalOpen(false);
  }

  function abrirModalExportacao() {
    setExportModalOpen(true);
  }

  function limparEstadoExportacao() {
    localStorage.removeItem(STORAGE_KEY);
  }

  function finalizarFalha(mensagem, taskId) {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    setExportState("failure");
    setExportErro(mensagem || "Não foi possível gerar a lista de empresas.");
    setExportModalOpen(true);
    limparEstadoExportacao();
  }

  function finalizarVazio(mensagem, taskId) {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    setExportState("vazio");
    setExportMensagem(mensagem || "Nenhum registro encontrado para os filtros selecionados.");
    setExportModalOpen(true);
    limparEstadoExportacao();
  }

  function finalizarSucesso(taskId, arquivo, mensagem) {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    setExportState("success");
    setExportFileName(arquivo);
    setExportMensagem(mensagem || "");
    setDownloadError("");
    setExportModalOpen(true);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      taskId, fileName: arquivo, status: "done", savedAt: Date.now(),
    }));
    dispararNotificacao();
    baixarArquivoTask(taskId).catch(() => {});
  }

  function schedulePoll(taskId, delayMs) {
    if (!isMountedRef.current) return;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(() => pollProgresso(taskId, delayMs), delayMs);
  }

  async function pollProgresso(taskId, baseIntervalMs) {
    try {
      const data = await verificarProgressoTask(taskId);
      if (!isMountedRef.current) return;
      failCountRef.current = 0;
      firstFailAtRef.current = null;

      const estado = (data.estado || data.status || "").toUpperCase();
      const progress = data.progresso ?? data.progress ?? data.porcentagem ?? 0;
      const registros = data.registros_processados ?? null;
      const mensagem = data.mensagem;

      if (["FAILURE", "ERROR", "ERRO", "ERROR_NEGOCIO"].includes(estado)) {
        finalizarFalha(data.erro || mensagem || "Não foi possível gerar a lista de empresas.", taskId);
        return;
      }

      if (estado === "SUCCESS") {
        const arquivo = data.arquivo;
        if (!arquivo || data.vazio) {
          finalizarVazio(mensagem, taskId);
          return;
        }
        finalizarSucesso(taskId, arquivo, mensagem);
        return;
      }

      // PROGRESS / PENDING
      setExportProgress(typeof progress === "number" ? progress : 0);
      setExportMensagem(mensagem || "");
      setExportRegistros(registros);
      setExportState("running");
      schedulePoll(taskId, baseIntervalMs);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (!erroDePollingTransitorio(err)) {
        finalizarFalha(err.status === 404 ? "Exportação não encontrada." : (err.detalhe || "Não foi possível verificar o progresso da exportação."), taskId);
        return;
      }
      failCountRef.current += 1;
      if (firstFailAtRef.current === null) firstFailAtRef.current = Date.now();
      if (Date.now() - firstFailAtRef.current >= MAX_POLL_TRANSIENT_MS) {
        if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
        setExportState("failure");
        setExportErro("Não conseguimos acompanhar agora. Sua lista continua sendo gerada e aparece no histórico.");
        setExportModalOpen(true);
        return;
      }
      const backoff = Math.max(
        Math.min(baseIntervalMs * Math.pow(1.5, failCountRef.current), 30000),
        err.retryAfterMs || 0
      );
      schedulePoll(taskId, backoff);
    }
  }

  async function handleDownload() {
    if (!exportTaskId) return;
    try {
      setDownloadError("");
      await baixarArquivoTask(exportTaskId);
    } catch (err) {
      setDownloadError(err.message || "Erro ao baixar o arquivo.");
      if (err.status === 403 || err.status === 404) {
        limparEstadoExportacao();
        setExportFileName(null);
      }
    }
  }

  function pedirPermissaoNotificacao() {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }

  function dispararNotificacao() {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("DadosPJ — Lista de empresas pronta!", {
        body: "Seu arquivo está pronto para download.",
        icon: "/favicon.ico",
      });
    }
  }

  // Ao montar: checar se há tarefa pendente ou concluída salva
  useEffect(() => {
    isMountedRef.current = true;
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const state = JSON.parse(saved);
        const age = Date.now() - (state.savedAt || 0);
        const MAX_AGE = 7 * 24 * 60 * 60 * 1000;
        if (age < MAX_AGE) {
          if (state.status === "done" && state.taskId && state.fileName) {
            setExportTaskId(state.taskId);
            setExportFileName(state.fileName);
            setExportProgress(100);
            setExportState("success");
          } else if (state.status === "running" && state.taskId) {
            setExportTaskId(state.taskId);
            setExportState("running");
            schedulePoll(state.taskId, 5000);
          }
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch { localStorage.removeItem(STORAGE_KEY); }
    return () => {
      isMountedRef.current = false;
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const multiUfAtivo = ufs.length > 1;

  useEffect(() => {
    if (ufs.length > 1) { setMunicipios([]); setBairrosSelecionados([]); }
  }, [ufs]);

  useEffect(() => {
    async function carregarBairros() {
      if (ufs.length !== 1) { setListaBairros([]); setBairrosSelecionados([]); return; }
      try {
        setCarregandoBairros(true);
        const bairros = await listarBairros(ufs[0], municipios);
        setListaBairros(bairros);
        setBairrosSelecionados((prev) => prev.filter((b) => bairros.includes(b)));
      } catch (err) { setListaBairros([]); }
      finally { setCarregandoBairros(false); }
    }
    carregarBairros();
  }, [ufs, municipios]);

  useEffect(() => {
    async function carregarAuxiliares() {
      try {
        setLoadingAuxiliares(true);
        const [respCidades, respCnaes] = await Promise.all([listarCidadesTodas(), listarCnaes()]);
        setCidadesPorUf(respCidades || {});
        if (Array.isArray(respCnaes)) {
          const parsed = respCnaes.map((item) => {
            if (typeof item !== "string") return { codigo: String(item), descricao: "" };
            const [codigo, ...rest] = item.split(" - ");
            return { codigo: (codigo || "").trim(), descricao: rest.join(" - ").trim() };
          });
          setListaCnaes(parsed);
        } else { setListaCnaes([]); }
      } catch (err) { console.error(err); }
      finally { setLoadingAuxiliares(false); }
    }
    carregarAuxiliares();
  }, []);

  useEffect(() => {
    if (ufs.length !== 1) { setCidadesDaUf([]); setMunicipios([]); setBairrosSelecionados([]); return; }
    const lista = cidadesPorUf[ufs[0]] || [];
    setCidadesDaUf(lista);
    setMunicipios((prev) => prev.filter((m) => lista.includes(m)));
  }, [ufs, cidadesPorUf]);

  function buildFiltros() {
    const ufsFinal = ufs.includes(BRASIL_TODO) ? [...TODAS_UFS] : [...ufs];
    const situacaoCodigos = situacaoCadastral.length > 0 ? situacaoCadastral : undefined;
    return {
      ufs: ufsFinal.length > 1 ? ufsFinal : undefined,
      uf: ufsFinal.length === 1 ? ufsFinal[0] : undefined,
      municipios: municipios.length ? municipios : undefined,
      cnae_fiscal: cnaesSelecionados.length ? cnaesSelecionados : undefined,
      busca_termo: buscaTermo || undefined,
      tipo_telefone: tipoTelefone || undefined,
      ddd: ddd.length ? ddd : undefined,
      porte_empresa: porteEmpresa.length > 0
        ? porteEmpresa.map((p) => {
          if (p === "ME - Microempresa") return "ME";
          if (p === "EPP - Empresa de Pequeno Porte") return "EPP";
          if (p === "DEMAS - Empresa de Médio ou Grande Porte") return "Demais";
          return p;
        })
        : undefined,
      MEINAOMEI: meiNaomei || undefined,
      data_inicio_atividades: dataInicioAtiv?.length === 10
        ? `${dataInicioAtiv.slice(6, 10)}${dataInicioAtiv.slice(3, 5)}${dataInicioAtiv.slice(0, 2)}`
        : undefined,
      formato: formato || undefined,
      bairros: bairrosSelecionados.length ? bairrosSelecionados : undefined,
      situacao_cadastral: situacaoCodigos,
    };
  }

  async function handleExportarConfirmado() {
    setExportErro(""); setExportState("running"); setExportProgress(0);
    setExportMensagem(""); setExportRegistros(null);
    setExportFileName(null); setDownloadError("");
    limparEstadoExportacao();
    setExportModalOpen(true);
    pedirPermissaoNotificacao();
    try {
      const filtros = buildFiltros();
      const resp = await iniciarExportacaoAssincrona(filtros);
      const taskId = resp.task_id;
      setExportTaskId(taskId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ taskId, status: "running", savedAt: Date.now() }));
      const retryAfterMs = (resp.retry_after || 5) * 1000;
      schedulePoll(taskId, retryAfterMs);
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("restritivos") || msg.includes("filtros insuficientes")) {
        setExportErro("Para gerar a lista de empresas selecione pelo menos um filtro adicional além da UF.");
      } else {
        setExportErro(msg || "Erro ao iniciar exportação.");
      }
      setExportState("failure");
    }
  }

  const listaCnaesFiltrada = listaCnaes
    .filter((item) => {
      const termo = cnaeBusca.trim().toLowerCase();
      if (!termo) return true;
      return item.codigo.toLowerCase().includes(termo) || item.descricao.toLowerCase().includes(termo);
    }).slice(0, 150);

  function handleSelecionarCnae(codigo) {
    setCnaesSelecionados((prev) =>
      prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]
    );
  }
  function handleRemoverCnae(codigo) {
    setCnaesSelecionados((prev) => prev.filter((c) => c !== codigo));
  }

  if (territorioSemLiberacao) {
    return (
      <main className="mf-main">
        <div className="mf-page-header">
          <div>
            <h1 className="mf-page-title">Filtros de Busca</h1>
            <p className="mf-page-sub">Monte sua lista de empresas com filtros rápidos e exporte em Excel/CSV.</p>
          </div>
        </div>
        <div className="mf-filters-card">
          <div className="confirm-alert">
            <AlertTriangle size={16} />
            <span>Sua plataforma ainda não foi liberada. Entre em contato com o suporte.</span>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="mf-main">

        {/* ── PAGE HEADER ── */}
        <div className="mf-page-header">
          <div>
            <h1 className="mf-page-title">Filtros de Busca</h1>
            <p className="mf-page-sub">Monte sua lista de empresas com filtros rápidos e exporte em Excel/CSV.</p>
          </div>
        </div>

        {/* ── LAYOUT 2 COLUNAS ── */}
        <div className="mf-two-col">

          {/* COLUNA ESQUERDA: vídeo + dicas */}
          <div className="mf-left-col">

            <div className="mf-video-card">
              <p className="mf-video-title">Como gerar sua lista de empresas</p>
              <div className="mf-video-wrap">
                <VideoYouTube id={VIDEO_TUTORIAL_EXTRACAO_ID} titulo="Tutorial de extração da lista de empresas" />
              </div>
              <div className="mf-quick-tip">
                Assista ao Rápido Tutorial para Evitar erros ao Extrair sua Lista
              </div>
            </div>

            <div className="mf-tips-card">
              <h3 className="mf-tips-title">Regras de Uso</h3>
              <div className="mf-tips-list">
                {[
                  { icon: "📍", title: "Estado e cidades", desc: "Você não pode alterar o Estado (UF) e deve escolher no mínimo 1 e no máximo 3 Cidades." },
                  { icon: "📞", title: "Contato por WhatsApp", desc: "Caso vá utilizar a lista para contatos via WhatsApp, use o filtro de apenas telefones Móveis." },
                  { icon: "🗓️", title: "Limite semanal", desc: "Você pode gerar 2 Listas por Semana." },
                ].map((tip, i) => (
                  <div key={i} className="mf-tip-item">
                    <span className="mf-tip-icon">{tip.icon}</span>
                    <div>
                      <strong>{tip.title}</strong>
                      <p>{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* COLUNA DIREITA: filtros */}
          <div className="mf-right-col">
            {/* ── FILTERS CARD ── */}
            <div className="mf-filters-card animate-fade-in-up" style={{ opacity: 0, animationDelay: "0.1s" }}>

              {loadingAuxiliares && (
                <p className="mf-loading-msg">Carregando listas de cidades e CNAEs...</p>
              )}

              {/* ── BUSCA POR TERMO ── */}
              <div className="mf-section">
                <label className="mf-field">
                  <span className="mf-label">
                    Buscar por termo (razão social ou fantasia)
                    <HelpTooltip text="Digite um termo específico ex: supermercado e clique em exportar arquivo" />
                  </span>
                  <input
                    className="mf-input"
                    value={buscaTermo}
                    onChange={(e) => setBuscaTermo(e.target.value)}
                    placeholder="Digite o termo de busca..."
                  />
                </label>
              </div>

              {/* ── LINHA 1: CNAEs / UF / Cidades / Situação / Porte ── */}
              <div className="mf-grid mf-grid--3">

                {/* CNAEs */}
                <div className="mf-field">
                  <span className="mf-label">
                    CNAEs
                    <HelpTooltip text="Selecione os CNAEs que representam o tipo de empresa que você deseja atingir." />
                  </span>
                  <button
                    type="button"
                    className="mf-input mf-input--btn"
                    onClick={() => setCnaeModalAberto(true)}
                  >
                    Digite para buscar...
                    <ChevronDown size={14} />
                  </button>
                  {cnaesSelecionados.length > 0 && (
                    <div className="mf-tags" style={{ marginTop: "0.4rem" }}>
                      {cnaesSelecionados.map((codigo) => {
                        const info = listaCnaes.find((c) => c.codigo === codigo);
                        const label = info ? `${codigo} - ${info.descricao}` : codigo;
                        return (
                          <span key={codigo} className="mf-tag">
                            {label}
                            <button type="button" onClick={() => handleRemoverCnae(codigo)}>×</button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* UF */}
                {ufUnica ? (
                  <div className="mf-field">
                    <span className="mf-label">
                      Estado
                      <HelpTooltip text="Seu plano libera apenas este estado." />
                    </span>
                    <div className="mf-input" style={{ opacity: 0.7, cursor: "not-allowed" }}>
                      {ufsPermitidas[0]}
                    </div>
                  </div>
                ) : territorioAtivo ? (
                  <MultiSelectDropdown
                    label={<>Estado <HelpTooltip text="Escolha um dos estados liberados para o seu plano." /></>}
                    placeholder="Selecione..."
                    options={ufsPermitidas}
                    selected={ufs}
                    onChange={(novaSelecao) => {
                      const novaUf = novaSelecao.find((v) => !ufs.includes(v));
                      setUfs(novaUf ? [novaUf] : []);
                    }}
                  />
                ) : (
                  <MultiSelectDropdown
                    label={<>UF(s) <HelpTooltip text="Você pode selecionar 1 ou mais estados." /></>}
                    placeholder="Selecione..."
                    options={[BRASIL_TODO, ...UF_LIST]}
                    selected={ufs}
                    onChange={setUfs}
                  />
                )}

                {/* Cidades */}
                <div style={multiUfAtivo ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
                  <MultiSelectDropdown
                    label={
                      <>
                        Cidades
                        <HelpTooltip text={territorioAtivo
                          ? "Selecione de 1 a 3 municípios do seu estado."
                          : "Você pode selecionar uma ou mais cidades."} />
                      </>
                    }
                    placeholder="Selecione uma UF p..."
                    options={cidadesDaUf}
                    selected={municipios}
                    onChange={(novaSelecao) => {
                      if (territorioAtivo && novaSelecao.length > 3) return;
                      setMunicipios(novaSelecao);
                    }}
                  />
                  {territorioAtivo && (
                    <p className="muted small" style={{ marginTop: "0.3rem" }}>
                      Selecione de 1 a 3 municípios.
                    </p>
                  )}
                </div>
              </div>

              {/* ── LINHA 2: Situação / Porte / Bairros ── */}
              <div className="mf-grid mf-grid--3">

                {/* Situação */}
                <MultiSelectDropdown
                  label={<>Situação <HelpTooltip text="Filtra empresas pela situação cadastral." /></>}
                  placeholder="Ativa"
                  options={SITUACAO_CADASTRAL_OPTIONS}
                  selected={situacaoCadastral}
                  onChange={setSituacaoCadastral}
                />

                {/* Porte */}
                <MultiSelectDropdown
                  label={<>Porte <HelpTooltip text="Filtre empresas pelo porte: ME, EPP ou Médio/Grande." /></>}
                  placeholder="Porte (Todos)"
                  options={PORTE_OPTIONS}
                  selected={porteEmpresa}
                  onChange={setPorteEmpresa}
                />

                {/* Bairros */}
                <div style={multiUfAtivo ? { pointerEvents: "none", opacity: 0.5 } : undefined}>
                  <MultiSelectDropdown
                    label={<>Bairros (opcional) <HelpTooltip text="Selecione a cidade primeiro; os bairros disponíveis aparecem aqui." /></>}
                    placeholder={carregandoBairros ? "Carregando bairros..." : "Selecione uma cidade primeiro"}
                    options={listaBairros}
                    selected={bairrosSelecionados}
                    onChange={setBairrosSelecionados}
                  />
                </div>
              </div>

              {/* ── LINHA 3: DDD / Data ── */}
              <div className="mf-grid mf-grid--2">

                {/* DDD */}
                <MultiSelectDropdown
                  label={<>DDD (opcional) <HelpTooltip text="Você pode selecionar um ou mais DDDs." /></>}
                  placeholder="Selecione..."
                  options={DDD_LIST}
                  selected={ddd}
                  onChange={setDdd}
                />

                {/* Data */}
                <div className="mf-field">
                  <span className="mf-label">
                    Data início atividades
                    <HelpTooltip text="Retorna empresas que iniciaram atividades até a data informada." />
                  </span>
                  <div className="mf-date-wrap">
                    <input
                      ref={dateInputRef}
                      className="mf-input"
                      type="text"
                      placeholder="DD/MM/YYYY"
                      value={dataInicioAtiv || ""}
                      onFocus={() => dateHiddenRef.current?.showPicker?.()}
                      onClick={() => dateHiddenRef.current?.showPicker?.()}
                      onChange={(e) => {
                        let v = e.target.value.replace(/\D/g, "").slice(0, 8);
                        if (v.length >= 5) v = `${v.slice(0, 2)}/${v.slice(2, 4)}/${v.slice(4)}`;
                        else if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
                        setDataInicioAtiv(v);
                      }}
                    />
                    <button
                      type="button"
                      className="mf-date-icon"
                      onClick={() => dateHiddenRef.current?.showPicker?.()}
                    >
                      <Calendar size={16} />
                    </button>
                    <input
                      ref={dateHiddenRef}
                      className="mf-date-hidden"
                      type="date"
                      value={
                        dataInicioAtiv?.length === 10
                          ? `${dataInicioAtiv.slice(6, 10)}-${dataInicioAtiv.slice(3, 5)}-${dataInicioAtiv.slice(0, 2)}`
                          : ""
                      }
                      onChange={(e) => {
                        const iso = e.target.value;
                        if (!iso) return setDataInicioAtiv("");
                        const [y, m, d] = iso.split("-");
                        setDataInicioAtiv(`${d}/${m}/${y}`);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* ── LINHA 4: Tipo empresa / Telefone / Formato ── */}
              <div className="mf-radio-row">

                {/* Tipo empresa */}
                <div className="mf-radio-group">
                  <span className="mf-label">
                    Tipo de empresa
                    <HelpTooltip text="Escolha empresas MEI e NÃO MEI" />
                  </span>
                  <div className="mf-radio-options">
                    {[
                      { value: "AMBOS", label: "Ambos" },
                      { value: "MEI", label: "Só MEI" },
                      { value: "NAOMEI", label: "Não MEI" },
                    ].map((opt) => (
                      <label key={opt.value} className="mf-radio-label">
                        <input
                          type="radio"
                          name="meiNaomei"
                          value={opt.value}
                          checked={meiNaomei === opt.value}
                          onChange={() => setMeiNaomei(opt.value)}
                        />
                        <span className="mf-radio-custom" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Telefone */}
                <div className="mf-radio-group">
                  <span className="mf-label">
                    Telefone
                    <HelpTooltip text="Selecione telefone móvel (whatsapp) ou Fixo" />
                  </span>
                  <div className="mf-radio-options">
                    {[
                      { value: "", label: "Fixo" },
                      { value: "movel", label: "Móvel" },
                      { value: "ambos", label: "Móveis + Fixos" },
                    ].map((opt) => (
                      <label key={opt.value} className="mf-radio-label">
                        <input
                          type="radio"
                          name="tipoTelefone"
                          value={opt.value}
                          checked={tipoTelefone === opt.value || (opt.value === "ambos" && tipoTelefone === "")}
                          onChange={() => setTipoTelefone(opt.value === "ambos" ? "" : opt.value)}
                        />
                        <span className="mf-radio-custom" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Formato */}
                <div className="mf-radio-group">
                  <span className="mf-label">
                    Formato do arquivo
                    <HelpTooltip text="Escolha o formato de exportação" />
                  </span>
                  <div className="mf-radio-options">
                    {[
                      { value: "xlsx", label: "Excel (.xlsx)" },
                      { value: "csv", label: "CSV (.csv)" },
                    ].map((opt) => (
                      <label key={opt.value} className="mf-radio-label">
                        <input
                          type="radio"
                          name="formato"
                          value={opt.value}
                          checked={formato === opt.value}
                          onChange={() => setFormato(opt.value)}
                        />
                        <span className="mf-radio-custom" />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* spacer para empurrar botões ao fundo */}
              <div style={{ flex: 1 }} />

              {/* botões secundários */}
              <div className="mf-actions mf-actions--hero">
                <button
                  type="button"
                  className="mf-btn-secondary"
                  onClick={() => {
                    setBuscaTermo("");
                    setUfs(ufUnica ? [ufsPermitidas[0]] : []);
                    setMunicipios(territorioAtivo ? [] : []);
                    setDdd([]);
                    setCnaesSelecionados([]); setTipoTelefone(""); setPorteEmpresa([]);
                    setMeiNaomei("NAOMEI"); setDataInicioAtiv(""); setSituacaoCadastral(["Ativa"]);
                    setFormato("xlsx");
                    setBairrosSelecionados([]);
                  }}
                >
                  LIMPAR FILTROS
                </button>
                <button
                  ref={exportBtnRef}
                  type="button"
                  className="mf-btn-primary mf-btn-primary--hero"
                  onClick={() => {
                    const filtros = buildFiltros();
                    const ufsFinal = [...(filtros.ufs || []), ...(filtros.uf ? [filtros.uf] : [])];
                    if (ufsFinal.length === 0) {
                      setUfErroModalOpen(true);
                      return;
                    }
                    setConfirmSnapshot(filtros);
                    setConfirmModalOpen(true);
                  }}
                  disabled={exportState === "running" || (territorioAtivo && (municipios.length < 1 || municipios.length > 3))}
                >
                  <FileSpreadsheet size={22} />
                  {exportState === "running" ? `Exportando... (${exportProgress}%)` : "EXPORTAR LISTA"}
                </button>
              </div>
              {territorioAtivo && (municipios.length < 1 || municipios.length > 3) && (
                <p className="mf-error" style={{ marginTop: "0.4rem" }}>
                  Selecione de 1 a 3 municípios.
                </p>
              )}

              {/* indicador inline — visível quando o modal de status está fechado */}
              {exportTaskId && !exportModalOpen && exportState === "running" && (
                <div className="mf-export-status">
                  <div className="mf-progress-bar">
                    <div className="mf-progress-fill" style={{ width: `${exportProgress}%` }} />
                  </div>
                  <div className="mf-export-status-text">
                    <span>{exportMensagem || `Gerando lista de empresas (${exportProgress}%)`}</span>
                    <button type="button" className="mf-btn-secondary" onClick={abrirModalExportacao}>
                      Ver detalhes
                    </button>
                  </div>
                </div>
              )}

              {/* Botão grande de download — aparece quando pronto e o modal está fechado */}
              {exportState === "success" && exportFileName && !exportModalOpen && (
                <div className="mf-download-ready-wrap">
                  <button
                    type="button"
                    className="mf-btn-download-ready"
                    onClick={handleDownload}
                  >
                    <Download size={22} />
                    <span>Baixar Arquivo Gerado</span>
                  </button>
                  <button
                    type="button"
                    className="mf-download-dismiss"
                    onClick={() => { setExportState(null); setExportFileName(null); limparEstadoExportacao(); }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
            {/* fecha mf-right-col */}
          </div>
          {/* fecha mf-two-col */}
        </div>
      </main>

      {/* ── MODAL CONFIRMAÇÃO ── */}
      {confirmModalOpen && (
        <div className="modal-backdrop" onClick={() => setConfirmModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <div className="confirm-title">
                <div className={`confirm-badge ${riscoAmplo ? "warn" : "ok"}`}>
                  <HeaderIcon size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Confirmação da Pesquisa</h3>
                  <p className="confirm-sub">
                    {filtrosCount} filtro(s) aplicados{" "}
                    {riscoAmplo ? "• Pode gerar muitos resultados" : "• Tudo pronto para exportar"}
                  </p>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={() => setConfirmModalOpen(false)}>×</button>
            </div>
            <p className="muted small" style={{ marginTop: 0 }}>
              Confira os filtros selecionados antes de iniciar a exportação.
            </p>
            <div className="confirm-list">
              {confirmItens.map((i) => {
                const Icon = i.icon;
                return (
                  <div className="confirm-row" key={i.key}>
                    <div className="confirm-left">
                      <span className="confirm-row-icon"><Icon size={16} /></span>
                      <span className="confirm-row-key">{i.key}</span>
                    </div>
                    <div className="confirm-row-value" title={String(i.value)}>{String(i.value)}</div>
                  </div>
                );
              })}
            </div>
            {riscoAmplo && (
              <div className="confirm-alert">
                <AlertTriangle size={16} />
                <span>Você selecionou poucos filtros. Isso pode gerar uma lista muito grande e demorar mais.</span>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "1rem" }}>
              <button type="button" className="btn-secondary" onClick={() => setConfirmModalOpen(false)}>Voltar</button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => { setConfirmModalOpen(false); handleExportarConfirmado(); }}
              >
                <ShieldCheck size={16} /> Confirmar e Exportar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL ERRO UF ── */}
      {ufErroModalOpen && (
        <div className="modal-backdrop" onClick={() => setUfErroModalOpen(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="confirm-header">
              <div className="confirm-title">
                <div className="confirm-badge warn">
                  <AlertTriangle size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>Estado não selecionado</h3>
                  <p className="confirm-sub">Filtro obrigatório</p>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={() => setUfErroModalOpen(false)}>×</button>
            </div>
            <p style={{ margin: "0.75rem 0 1.25rem", fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.6 }}>
              Selecione pelo menos <strong style={{ color: "var(--text-primary)" }}>1 estado (UF)</strong> antes de exportar.
              O filtro de UF é obrigatório para todas as exportações.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn-primary" onClick={() => setUfErroModalOpen(false)}>
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DE STATUS DA EXPORTAÇÃO ── */}
      <ExportStatusModal
        open={exportModalOpen}
        state={exportState}
        progress={exportProgress}
        mensagem={exportMensagem}
        registrosProcessados={exportRegistros}
        erro={exportErro}
        downloadError={downloadError}
        fileName={exportFileName}
        onClose={fecharModalExportacao}
        onDownload={handleDownload}
        triggerRef={exportBtnRef}
      />

      {/* ── MODAL CNAE ── */}
      {cnaeModalAberto && (
        <div className="modal-backdrop" onClick={() => setCnaeModalAberto(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0 }}>Selecionar CNAEs</h3>
              <div style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
                {cnaesSelecionados.length > 0 && (
                  <button type="button" className="multi-clear" onClick={() => setCnaesSelecionados([])}>Limpar</button>
                )}
                <button type="button" className="multi-exit" onClick={() => setCnaeModalAberto(false)}>Salvar</button>
              </div>
            </div>
            <input
              className="modal-search"
              placeholder="Digite código ou descrição..."
              value={cnaeBusca}
              onChange={(e) => setCnaeBusca(e.target.value)}
              style={{
                width: "100%", marginBottom: "1rem",
                background: "rgba(2,6,23,0.7)",
                border: "1px solid rgba(34,211,238,0.2)",
                borderRadius: "0.75rem",
                padding: "0.7rem 1rem",
                color: "#e5e7eb",
              }}
            />
            <div className="modal-list">
              {listaCnaesFiltrada.map((item) => (
                <button
                  key={item.codigo + item.descricao}
                  type="button"
                  className={`modal-item${cnaesSelecionados.includes(item.codigo) ? " modal-item-active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); handleSelecionarCnae(item.codigo); }}
                >
                  <span className="modal-item-code">{item.codigo}</span>
                  <span className="modal-item-desc">{item.descricao}</span>
                </button>
              ))}
              {cnaesSelecionados.length === 0 ? (
                <span className="muted small">Nenhum CNAE selecionado.</span>
              ) : (
                <div className="selected-tags">
                  {cnaesSelecionados.map((codigo) => (
                    <span key={codigo} className="mf-tag">
                      {codigo}
                      <button type="button" onClick={() => handleRemoverCnae(codigo)}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
